// frontend-react/src/components/BoxScore/BoxScoreSummary.js
// Game summary statistics for box score display

import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import RankingsModal from './RankingsModal';

/**
 * BoxScoreSummary Component
 * Displays key game summary statistics in two rows (hitting & pitching)
 * Based on league scoring categories
 *
 * @param {Object} teamTotals - Team totals for hitting and pitching
 * @param {Object} scoringCategories - League scoring categories {hitting: [], pitching: []}
 * @param {Object} rankingsData - Full rankings response {your_ranks: {}, all_rankings: {}, stats_date: ''}
 * @param {string} yourTeamId - Current user's team ID
 * @param {Array} allTeams - All teams for dropdown
 * @param {string} selectedTeamId - Currently selected team ID
 * @param {function} onTeamChange - Handler for team selection change
 * @param {boolean} teamsLoading - Whether teams are loading
 */
const BoxScoreSummary = ({
  teamTotals = null,
  scoringCategories = null,
  rankingsData = null,
  yourTeamId = null,
  loading = false,
  allTeams = [],
  selectedTeamId = null,
  onTeamChange = null,
  teamsLoading = false
}) => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('total_hr');

  // Extract rankings from rankingsData
  const rankings = rankingsData?.your_ranks || null;
  const allRankings = rankingsData?.all_rankings || null;
  const statsDate = rankingsData?.stats_date || '';
  // Chunk 3.14 — tile-enrichment fields from backend (Phase A).
  const yourTeamWeekly = rankingsData?.your_team_weekly || null;
  const yourTeamSeason = rankingsData?.your_team_season || null;
  const yourTeamHistory = rankingsData?.your_team_weekly_history || null;
  const yourTeamWow = rankingsData?.your_team_wow || null;

  // Handler to open modal with a specific stat category
  const handleTileClick = (category) => {
    if (!allRankings) return; // Don't open if no data
    setSelectedCategory(category);
    setIsModalOpen(true);
  };
  if (loading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
          📊 Game Summary
        </h3>
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center gap-3">
            <div 
              className={`w-6 h-6 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`}
            />
            <span className={dynastyTheme.classes.text.neutralLight}>
              Loading summary...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!teamTotals) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
          📊 Game Summary
        </h3>
        <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
          No summary stats available for this date
        </div>
      </div>
    );
  }

  // Default scoring categories if not provided
  const hittingCategories = scoringCategories?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'];
  const pitchingCategories = scoringCategories?.pitching || ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS'];

  // Helper to get rank suffix (1st, 2nd, 3rd, etc.)
  const getRankSuffix = (num) => {
    if (num % 10 === 1 && num !== 11) return 'st';
    if (num % 10 === 2 && num !== 12) return 'nd';
    if (num % 10 === 3 && num !== 13) return 'rd';
    return 'th';
  };

  // Helper to get rank styling
  const getRankStyling = (rank) => {
    if (!rank) return { containerClass: '', badgeClass: '', showBadge: false };
    
    let containerClass = 'relative ';
    let badgeClass = '';
    let emoji = '';
    
    if (rank.is_first) {
      // 🔥 Rank #1: Gold border + glow + pulsing animation
      containerClass += 'border-3 border-yellow-500 shadow-[0_0_20px_rgba(255,215,0,0.6)] animate-[pulse_2s_ease-in-out_infinite]';
      badgeClass = 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black';
      emoji = '🔥';
    } else if (rank.rank === 2) {
      // 🥈 Rank #2: Silver
      containerClass += 'border-2 border-gray-300 shadow-[0_0_15px_rgba(192,192,192,0.4)]';
      badgeClass = 'bg-gradient-to-br from-gray-300 to-gray-400 text-black';
    } else if (rank.rank === 3) {
      // 🥉 Rank #3: Bronze
      containerClass += 'border-2 border-amber-600 shadow-[0_0_15px_rgba(205,127,50,0.4)]';
      badgeClass = 'bg-gradient-to-br from-amber-600 to-amber-700 text-white';
    } else if (rank.is_last) {
      // ❄️ Last Place: Blue/ice
      containerClass += 'border-2 border-blue-400 shadow-[0_0_15px_rgba(74,144,226,0.3)] opacity-85';
      badgeClass = 'bg-gradient-to-br from-blue-400 to-blue-600 text-white';
      emoji = '❄️';
    } else {
      containerClass += 'border border-neutral-700';
      badgeClass = 'bg-neutral-700 text-neutral-400';
    }
    
    const rankText = `${rank.rank}${getRankSuffix(rank.rank)} of ${rank.total_teams}`;
    
    return { containerClass, badgeClass, rankText, emoji, showBadge: true };
  };

  // Map category codes to ranking keys
  const rankingKeyMap = {
    'R': 'total_runs',
    'HR': 'total_hr',
    'RBI': 'total_rbi',
    'SB': 'total_sb',
    'AVG': 'avg',
    'OPS': 'ops',
    'W': 'total_wins',
    'SV': 'total_saves',
    'ERA': 'era',
    'WHIP': 'whip',
    'SO': 'total_k',
    'QS': 'total_qs'
  };

  // Map category codes to display info
  const statMap = {
    // Hitting stats
    'R': {
      label: 'Runs',
      key: 'runs',
      format: (val) => val || 0,
      highlight: true
    },
    'HR': {
      label: 'Home Runs',
      key: 'home_runs',
      format: (val) => val || 0,
      highlight: true
    },
    'RBI': {
      label: 'RBI',
      key: 'rbi',
      format: (val) => val || 0,
      highlight: false
    },
    'SB': {
      label: 'Stolen Bases',
      key: 'stolen_bases',
      format: (val) => val || 0,
      highlight: false
    },
    'AVG': {
      label: 'AVG',
      key: 'avg',
      format: (val) => formatBattingAvg(val),
      highlight: false
    },
    'OPS': {
      label: 'OPS',
      key: 'ops',
      format: (val) => formatBattingAvg(val),
      highlight: false
    },
    // Pitching stats
    'W': {
      label: 'Wins',
      key: 'wins',
      format: (val) => val || 0,
      highlight: true
    },
    'SV': {
      label: 'Saves',
      key: 'saves',
      format: (val) => val || 0,
      highlight: false
    },
    'ERA': {
      label: 'ERA',
      key: 'era',
      format: (val) => formatERA(val),
      highlight: false
    },
    'WHIP': {
      label: 'WHIP',
      key: 'whip',
      format: (val) => formatBattingAvg(val),
      highlight: false
    },
    'SO': {
      label: 'Strikeouts',
      key: 'strikeouts_pitched',
      format: (val) => val || 0,
      highlight: true
    },
    'QS': {
      label: 'Quality Starts',
      key: 'quality_starts',
      format: (val) => val || 0,
      highlight: false
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Chunk 3.14 — tile-enrichment helpers (subline + sparkline + delta).
  // Pattern mirrors the LastNightBox.js Phase B treatment (commit 3c110110)
  // applied to the same tile chrome. Sister-method parity per Bible Rule 11.
  // ────────────────────────────────────────────────────────────────────────

  const formatStat = (rankKey, v) => {
    if (v === null || v === undefined) return '—';
    if (rankKey === 'era') return formatERA(v);
    if (rankKey === 'avg' || rankKey === 'ops' || rankKey === 'whip') return formatBattingAvg(v);
    return v;
  };
  const formatDelta = (rankKey, delta) => {
    if (delta === null || delta === undefined) return '—';
    const isRate = ['avg', 'ops', 'era', 'whip'].includes(rankKey);
    if (isRate) {
      if (rankKey === 'era') return `${delta >= 0 ? '+' : ''}${Number(delta).toFixed(2)}`;
      return `${delta >= 0 ? '+' : ''}${Number(delta).toFixed(3).replace(/^0\./, '.').replace(/^-0\./, '-.')}`;
    }
    return `${delta >= 0 ? '+' : ''}${delta}`;
  };
  const renderTrendIcon = (direction) => {
    if (direction === 'up') return (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M6 2 L10 8 L2 8 Z" fill="currentColor"/></svg>
    );
    if (direction === 'down') return (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M6 10 L10 4 L2 4 Z" fill="currentColor"/></svg>
    );
    if (direction === 'flat') return (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
    );
    return (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeDasharray="2,2"/></svg>
    );
  };
  const trendColor = (direction) => {
    if (direction === 'up') return 'text-emerald-400';
    if (direction === 'down') return 'text-red-400';
    if (direction === 'flat') return 'text-zinc-400';
    return 'text-zinc-600';
  };

  // 6-point SVG sparkline with segmented polylines for null-gap handling.
  // Direction-of-good colors the stroke + gradient. Latest point highlighted.
  const renderSparkline = (history, direction, rankingKey) => {
    if (!history || history.length === 0) return null;
    const pts = history.slice(0, 6);
    const numeric = pts.filter(v => v !== null && v !== undefined).map(Number);
    if (numeric.length === 0) {
      return (
        <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '64px', height: '22px' }}>
          <line x1="0" y1="14" x2="100" y2="14" stroke="#3f3f46" strokeWidth="1" strokeDasharray="3,3" />
        </svg>
      );
    }
    const minV = Math.min(...numeric);
    const maxV = Math.max(...numeric);
    const range = maxV - minV || 1;
    const xs = [0, 20, 40, 60, 80, 100];
    const ys = pts.map(v => (v === null || v === undefined) ? null : 26 - ((v - minV) / range) * 22);
    const strokeColor =
      direction === 'up'   ? '#34d399' :
      direction === 'down' ? '#f87171' :
      direction === 'flat' ? '#a1a1aa' :
      '#52525b';
    const segments = [];
    let current = [];
    for (let i = 0; i < 6; i++) {
      if (ys[i] === null) {
        if (current.length >= 2) segments.push(current);
        current = [];
      } else {
        current.push(`${xs[i]},${ys[i]}`);
      }
    }
    if (current.length >= 2) segments.push(current);
    const lastIdx = ys.map((y, i) => y !== null ? i : -1).filter(i => i >= 0).pop();
    const lastX = lastIdx != null ? xs[lastIdx] : null;
    const lastY = lastIdx != null ? ys[lastIdx] : null;
    return (
      <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '64px', height: '22px' }}>
        {segments.map((seg, i) => (
          <polyline
            key={i}
            points={seg.join(' ')}
            fill="none"
            stroke={strokeColor}
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {lastX !== null && lastY !== null && (
          <>
            <circle cx={lastX} cy={lastY} r="3" fill={strokeColor} opacity="0.3" />
            <circle cx={lastX} cy={lastY} r="1.7" fill={strokeColor} />
          </>
        )}
      </svg>
    );
  };

  // Single tile renderer used for both hitting + pitching. `dataNamespace` is
  // 'hitting' or 'pitching' — picks which sub-tree of teamTotals to read for
  // the daily value display.
  const renderEnrichedTile = (cat, dataNamespace) => {
    const stat = statMap[cat];
    if (!stat) return null;

    const value = teamTotals?.[dataNamespace]?.[stat.key];
    const rankingKey = rankingKeyMap[cat];
    const rank = rankings?.[rankingKey];
    const { containerClass, badgeClass, rankText, emoji, showBadge } = getRankStyling(rank);

    // Chunk 3.14 — pull enrichment per stat.
    const weekVal = yourTeamWeekly?.[rankingKey];
    const seasonVal = yourTeamSeason?.[rankingKey];
    const wow = yourTeamWow?.[rankingKey];
    const history = yourTeamHistory?.[rankingKey];
    const direction = wow?.direction || 'none';
    const delta = wow?.delta;

    // Show the enrichment block only when the backend actually returned
    // enrichment for THIS stat (legacy / pre-Phase-A responses → no block).
    const hasEnrichment = (weekVal !== undefined || seasonVal !== undefined || history);

    return (
      <div
        key={cat}
        onClick={() => handleTileClick(rankingKey)}
        className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${containerClass} ${
          allRankings ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        }`}
      >
        {/* Rank Badge */}
        {showBadge && (
          <div className={`absolute top-2 right-2 ${badgeClass} px-2 py-1 rounded text-[10px] font-bold z-10`}>
            {rankText}
          </div>
        )}

        {/* Emoji for top/bottom ranks */}
        {emoji && (
          <div className="absolute bottom-1 right-1 text-lg z-10">
            {emoji}
          </div>
        )}

        <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
          {stat.label}
        </div>
        <div className={`text-xl font-bold ${
          stat.highlight ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.white
        }`}>
          {stat.format(value)}
        </div>

        {/* Chunk 3.14 — subline: Wk/Szn + sparkline + delta */}
        {hasEnrichment && (
          <>
            <div className="flex items-center gap-3 mt-2 pt-2" style={{ borderTop: '1px solid rgba(82,82,91,0.25)' }}>
              <div>
                <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-600">Wk</div>
                <div className="text-[11px] font-semibold text-zinc-300 tabular-nums">{formatStat(rankingKey, weekVal)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-600">Szn</div>
                <div className="text-[11px] font-semibold text-zinc-300 tabular-nums">{formatStat(rankingKey, seasonVal)}</div>
              </div>
            </div>
            <div className="flex items-end justify-between mt-1.5">
              {renderSparkline(history, direction, rankingKey)}
              <div className={`flex items-center gap-1 ${trendColor(direction)}`}>
                {renderTrendIcon(direction)}
                <span className="text-[11px] font-semibold tabular-nums">{formatDelta(rankingKey, delta)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const hittingTiles = hittingCategories.map(cat => renderEnrichedTile(cat, 'hitting')).filter(Boolean);
  const pitchingTiles = pitchingCategories.map(cat => renderEnrichedTile(cat, 'pitching')).filter(Boolean);

  return (
    <div className={`${dynastyTheme.components.card.base} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            📊 Daily Box Score
          </h3>
        </div>

        {/* Team Selection Dropdown */}
        {allTeams.length > 0 && onTeamChange && (
          <div className="flex items-center gap-2">
            <label className={`${dynastyTheme.classes.text.neutralLight} text-sm font-medium`}>
              View Team:
            </label>
            <select
              value={selectedTeamId || ''}
              onChange={onTeamChange}
              disabled={teamsLoading || loading}
              className={`${dynastyTheme.components.input} px-3 py-1.5 min-w-[200px] text-sm`}
            >
              {allTeams.map(team => {
                const suffix = team.is_user_team ? ' (Your Team)' : ` (${team.manager_name})`;
                return (
                  <option key={team.team_id} value={team.team_id}>
                    {team.team_name}{suffix}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>
      
      {/* Hitting Stats Row */}
      <div className="mb-3">
        <div className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
          ⚾ Hitting
        </div>
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${hittingTiles.length}, minmax(0, 1fr))` }}>
          {hittingTiles}
        </div>
      </div>

      {/* Pitching Stats Row */}
      <div>
        <div className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
          🥎 Pitching
        </div>
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${pitchingTiles.length}, minmax(0, 1fr))` }}>
          {pitchingTiles}
        </div>
      </div>

      {/* Rankings Modal */}
      <RankingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        allRankings={allRankings}
        yourTeamId={yourTeamId}
        defaultCategory={selectedCategory}
        statsDate={statsDate}
      />
    </div>
  );
};

export default BoxScoreSummary;
